import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "@/stores/auth";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

export interface ApiErrorBody {
  error: true;
  status_code: number;
  code: string;
  detail: string;
  fields?: Record<string, string[]>;
  trace_id: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  fields?: Record<string, string[]>;
  traceId: string;

  constructor(body: ApiErrorBody) {
    super(body.detail);
    this.status = body.status_code;
    this.code = body.code;
    this.fields = body.fields;
    this.traceId = body.trace_id;
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 401 && original && !original._retry && useAuthStore.getState().refreshToken) {
      original._retry = true;
      const newAccess = await tryRefresh();
      if (newAccess) {
        original.headers?.set("Authorization", `Bearer ${newAccess}`);
        return api.request(original);
      }
      useAuthStore.getState().clear();
    }

    if (data) {
      return Promise.reject(new ApiError(data));
    }
    return Promise.reject(error);
  },
);

async function tryRefresh(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) return null;
      try {
        const response = await axios.post<{
          access_token: string;
          refresh_token: string;
          expires_at: string;
        }>(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );
        useAuthStore.getState().setTokens({
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token,
          expiresAt: response.data.expires_at,
        });
        return response.data.access_token;
      } catch {
        return null;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}
